/**
 * PGLP | MONTE HERMOM
 * COMPONENTE: PrazoBar (v2.0.0)
 * ============================================================
 */

import React, { useMemo, useState } from 'react';
import {
  Clock, AlertTriangle, ShieldAlert, CheckCircle2, XCircle,
  Calendar, TrendingDown, Lock, Info,
} from 'lucide-react';
import PrazoDetalhesModal from './PrazoDetalhesModal';
import PrazoAcoesCriticas from './PrazoAcoesCriticas';

const CORES = {
  NORMAL:    { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', label: 'Normal',    barra: 'bg-emerald-500' },
  ATENCAO:   { bg: 'bg-amber-500/10',   border: 'border-amber-500/30',   text: 'text-amber-400',   label: 'Atenção',   barra: 'bg-amber-500' },
  RISCO:     { bg: 'bg-orange-500/10',  border: 'border-orange-500/30',  text: 'text-orange-400',  label: 'Risco',     barra: 'bg-orange-500' },
  CRITICO:   { bg: 'bg-rose-500/10',    border: 'border-rose-500/30',    text: 'text-rose-400',    label: 'Crítico',   barra: 'bg-rose-500' },
  CONSUMIDO: { bg: 'bg-slate-500/10',   border: 'border-slate-500/30',   text: 'text-slate-400',   label: 'Consumido', barra: 'bg-slate-500' },
};

export default function PrazoBar({
  estado,
  compacto = false,
  onClick,
  onRegistrarProvidencia,
  onPrepararProrrogacao,
  mostrarAcoesCriticas = true,
}) {
  const [modalAberto, setModalAberto] = useState(false);

  const cor = CORES[estado?.nivel_risco] || CORES.NORMAL;

  const pct = useMemo(() => {
    if (!estado || !estado.horas_totais || estado.horas_totais <= 0) return 0;
    return Math.min(100, Math.round((estado.horas_consumidas / estado.horas_totais) * 100));
  }, [estado]);

  const Icone = {
    NORMAL:    CheckCircle2,
    ATENCAO:   Clock,
    RISCO:     AlertTriangle,
    CRITICO:   ShieldAlert,
    CONSUMIDO: XCircle,
  }[estado?.nivel_risco] || CheckCircle2;

  const dataLimiteFmt = useMemo(() => {
    if (!estado?.data_limite_atual) return '—';
    try {
      return new Date(estado.data_limite_atual).toLocaleDateString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
      });
    } catch { return estado.data_limite_atual; }
  }, [estado?.data_limite_atual]);

  if (!estado) return null;

  const abrirModal = () => {
    if (typeof onClick === 'function') onClick();
    else setModalAberto(true);
  };

  const mostrarBotoes = mostrarAcoesCriticas
    && ['RISCO', 'CRITICO', 'CONSUMIDO'].includes(estado.nivel_risco)
    && (typeof onRegistrarProvidencia === 'function' || typeof onPrepararProrrogacao === 'function');

  /* COMPACTO */
  if (compacto) {
    return (
      <>
        <button
          type="button"
          onClick={abrirModal}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${cor.bg} ${cor.border} ${cor.text} hover:opacity-90 transition-all`}
          title={`${cor.label} · Margem ${estado.margem_atual}h — clique para detalhes`}
        >
          <Icone size={11} />
          <span className="text-[9px] font-black uppercase tracking-widest">
            {cor.label} · {pct}%
          </span>
        </button>

        {modalAberto && (
          <PrazoDetalhesModal estado={estado} onFechar={() => setModalAberto(false)} />
        )}
      </>
    );
  }

  /* COMPLETO */
  return (
    <>
      <div className="space-y-2">
        {/* BARRA CLICÁVEL */}
        <button
          type="button"
          onClick={abrirModal}
          className={`w-full text-left rounded-2xl border px-5 py-3 ${cor.bg} ${cor.border} hover:opacity-95 transition-all group`}
        >
          <div className="flex items-center justify-between gap-4 flex-wrap">

            <div className="flex items-center gap-3 shrink-0">
              <Icone size={16} className={cor.text} />
              <div className="leading-none">
                <p className="text-[8px] font-black text-slate-500 uppercase tracking-[0.3em]">
                  Prazo Operacional
                </p>
                <p className={`text-[12px] font-black uppercase italic tracking-tight ${cor.text}`}>
                  {cor.label}
                </p>
              </div>
            </div>

            <div className="flex-1 min-w-[200px]">
              <div className="flex justify-between text-[8px] font-black uppercase tracking-widest text-slate-500 mb-1">
                <span>Consumido: {estado.horas_consumidas}h de {estado.horas_totais}h</span>
                <span>{pct}%</span>
              </div>
              <div className="w-full h-2 bg-slate-950 rounded-full overflow-hidden border border-white/5">
                <div
                  className={`h-full transition-all ${cor.barra}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>

            <div className="flex items-center gap-5 shrink-0 flex-wrap">
              <div className="flex items-center gap-1.5">
                <Calendar size={11} className="text-slate-500" />
                <div className="leading-none">
                  <p className="text-[7px] font-black text-slate-500 uppercase tracking-widest">Limite</p>
                  <p className="text-[10px] font-bold text-white">{dataLimiteFmt}</p>
                </div>
              </div>

              <div className="flex items-center gap-1.5">
                <TrendingDown size={11} className="text-slate-500" />
                <div className="leading-none">
                  <p className="text-[7px] font-black text-slate-500 uppercase tracking-widest">Margem</p>
                  <p className="text-[10px] font-bold text-white">{estado.margem_atual}h</p>
                </div>
              </div>

              {estado.em_suspensao === 1 && (
                <div className="flex items-center gap-1.5 text-slate-400">
                  <Lock size={11} />
                  <span className="text-[8px] font-black uppercase tracking-widest">Suspenso</span>
                </div>
              )}

              {estado.possui_prorrogacao === 1 && (
                <span className="text-[8px] font-black uppercase tracking-widest text-amber-400">
                  +{estado.qtd_prorrogacoes || 0} prorr.
                </span>
              )}

              <div className="flex items-center gap-1 text-slate-500 group-hover:text-amber-400 transition-all">
                <Info size={11} />
                <span className="text-[8px] font-black uppercase tracking-widest">
                  Detalhes
                </span>
              </div>
            </div>
          </div>
        </button>

        {/* BOTÕES CRÍTICOS */}
        {mostrarBotoes && (
          <PrazoAcoesCriticas
            estado={estado}
            onRegistrarProvidencia={onRegistrarProvidencia}
            onPrepararProrrogacao={onPrepararProrrogacao}
          />
        )}
      </div>

      {modalAberto && (
        <PrazoDetalhesModal estado={estado} onFechar={() => setModalAberto(false)} />
      )}
    </>
  );
}